import type { SVGProps } from 'react';
import type { IconName } from './icon-names';

export type { IconName } from './icon-names';

interface Props extends SVGProps<SVGSVGElement> {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 16, ...rest }: Props) {
  const base = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    ...rest,
  };
  switch (name) {
    case 'dashboard':
      return (
        <svg {...base}>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
      );
    case 'chart':
      return (
        <svg {...base}>
          <path d="M3 3v18h18" />
          <path d="M7 14l4-4 3 3 5-7" />
        </svg>
      );
    case 'plus':
      return (
        <svg {...base}>
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      );
    case 'clock':
      return (
        <svg {...base}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case 'menu':
      return (
        <svg {...base}>
          <line x1="4" y1="7" x2="20" y2="7" />
          <line x1="4" y1="12" x2="20" y2="12" />
          <line x1="4" y1="17" x2="20" y2="17" />
        </svg>
      );
    case 'scenarios':
      return (
        <svg {...base}>
          <path d="M6 3v18" />
          <path d="M6 7h10a3 3 0 0 1 0 6H6" />
          <path d="M6 17h13" />
        </svg>
      );
    case 'investments':
      // Stacked coins / growth chart hybrid: signals "money that grows".
      return (
        <svg {...base}>
          <ellipse cx="12" cy="6" rx="7" ry="2.5" />
          <path d="M5 6v5c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5V6" />
          <path d="M5 11v5c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5v-5" />
        </svg>
      );
    case 'timeline':
      return (
        <svg {...base}>
          <circle cx="5" cy="12" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="19" cy="12" r="2" />
          <line x1="7" y1="12" x2="10" y2="12" />
          <line x1="14" y1="12" x2="17" y2="12" />
        </svg>
      );
    case 'check':
      return (
        <svg {...base}>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    case 'x':
      return (
        <svg {...base}>
          <line x1="6" y1="6" x2="18" y2="18" />
          <line x1="18" y1="6" x2="6" y2="18" />
        </svg>
      );
    case 'chevron-right':
      return (
        <svg {...base}>
          <polyline points="9 18 15 12 9 6" />
        </svg>
      );
    case 'chevron-down':
      return (
        <svg {...base}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      );
    case 'edit':
      return (
        <svg {...base}>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
        </svg>
      );
    case 'trash':
      return (
        <svg {...base}>
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
        </svg>
      );
    case 'copy':
      return (
        <svg {...base}>
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      );
    case 'download':
      return (
        <svg {...base}>
          <path d="M12 3v12" />
          <polyline points="6 11 12 17 18 11" />
          <path d="M5 21h14" />
        </svg>
      );
    case 'upload':
      return (
        <svg {...base}>
          <path d="M12 17V5" />
          <polyline points="6 9 12 3 18 9" />
          <path d="M5 21h14" />
        </svg>
      );
    case 'home':
      return (
        <svg {...base}>
          <path d="M3 10.5L12 3l9 7.5V21a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1Z" />
        </svg>
      );
    case 'home-sell':
      return (
        <svg {...base}>
          <path d="M3 10.5L12 3l9 7.5V21a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1Z" />
          <line x1="15" y1="3" x2="22" y2="10" />
        </svg>
      );
    case 'home-buy':
      return (
        <svg {...base}>
          <path d="M3 10.5L12 3l9 7.5V21a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1Z" />
          <line x1="12" y1="9" x2="12" y2="15" />
          <line x1="9" y1="12" x2="15" y2="12" />
        </svg>
      );
    case 'refi':
      return (
        <svg {...base}>
          <polyline points="1 4 1 10 7 10" />
          <polyline points="23 20 23 14 17 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
      );
    case 'income':
      return (
        <svg {...base}>
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
          <polyline points="17 6 23 6 23 12" />
        </svg>
      );
    case 'job-gap':
      return (
        <svg {...base}>
          <circle cx="12" cy="12" r="9" />
          <path d="M8 8a4 4 0 1 1 0 8" />
          <line x1="12" y1="16" x2="16" y2="16" />
        </svg>
      );
    case 'windfall':
      return (
        <svg {...base}>
          <path d="M20 12l-8 8-8-8" />
          <path d="M12 20V4" />
        </svg>
      );
    case 'gift':
      return (
        <svg {...base}>
          <polyline points="20 12 20 22 4 22 4 12" />
          <rect x="2" y="7" width="20" height="5" />
          <line x1="12" y1="22" x2="12" y2="7" />
          <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7Z" />
          <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7Z" />
        </svg>
      );
    case 'roth':
      return (
        <svg {...base}>
          <polyline points="17 1 21 5 17 9" />
          <path d="M3 11V9a4 4 0 0 1 4-4h14" />
          <polyline points="7 23 3 19 7 15" />
          <path d="M21 13v2a4 4 0 0 1-4 4H3" />
        </svg>
      );
    case 'pension':
      return (
        <svg {...base}>
          <rect x="3" y="6" width="18" height="13" rx="2" />
          <line x1="3" y1="10" x2="21" y2="10" />
          <line x1="8" y1="15" x2="10" y2="15" />
        </svg>
      );
    case 'retire':
      return (
        <svg {...base}>
          <path d="M4 22V4l14 6-14 6" />
        </svg>
      );
    case 'purchase':
      return (
        <svg {...base}>
          <path d="M5 7h14l-1.5 11a2 2 0 0 1-2 1.8h-7a2 2 0 0 1-2-1.8Z" />
          <path d="M9 7V5a3 3 0 0 1 6 0v2" />
        </svg>
      );
    case 'tag':
      return (
        <svg {...base}>
          <path d="M20 12L12 20l-9-9V4h7Z" />
          <circle cx="7" cy="7" r="1.5" />
        </svg>
      );
    case 'sparkle':
      return (
        <svg {...base}>
          <path d="M12 2l1.5 5L19 8.5 14 10l-2 5-2-5-5-1.5L10.5 7Z" />
        </svg>
      );
    case 'warning':
      return (
        <svg {...base}>
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case 'info':
      return (
        <svg {...base}>
          <circle cx="12" cy="12" r="9" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      );
    case 'cohorts':
      // A small bar-distribution: bars of varying heights, with a marker
      // indicating "you are here" — fits the percentile-comparison theme.
      return (
        <svg {...base}>
          <line x1="3" y1="20" x2="21" y2="20" />
          <rect x="4" y="14" width="3" height="6" rx="0.5" />
          <rect x="8.5" y="10" width="3" height="10" rx="0.5" />
          <rect x="13" y="6" width="3" height="14" rx="0.5" />
          <rect x="17.5" y="12" width="3" height="8" rx="0.5" />
          <path d="M14.5 3l-1.5 2h3z" fill="currentColor" stroke="none" />
        </svg>
      );
  }
}
